import { default as React, useLayoutEffect } from 'react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const STANDARD_TEMPLATES = [
  {
    id: 'angular-app',
    name: 'Angular App',
    description:
      'Angular 19 with TypeScript, SCSS, Jest, Router, ESLint, Prettier, and Docker',
  },
  {
    id: 'terraform-module',
    name: 'Terraform Module',
    description:
      'Reusable Terraform module (e.g., terraform-gcp-bucket, terraform-azure-devportal)',
  },
  {
    id: 'terraform-infrastructure',
    name: 'Terraform Infrastructure',
    description:
      'Infrastructure as Code for managing cloud resources (e.g., terraform-hhh-skystage)',
  },
];

const StandardTemplatePicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, required, formData, formContext } = props;

  // Get templateSource and repoType from form context
  const allFormData = formContext?.formData || {};
  const templateSource = allFormData.templateSource || '';
  const repoType = allFormData.repoType || 'standard';

  // Track if we've cleared the value to avoid redundant calls
  const hasClearedRef = React.useRef(false);

  // Filter templates based on repoType - Terraform templates only available for Standard repos
  const availableTemplates = React.useMemo(() => {
    if (repoType === 'Standard') {
      return STANDARD_TEMPLATES;
    }
    // For non-Standard repos (e.g., Playground), exclude Terraform templates
    return STANDARD_TEMPLATES.filter(t => !t.id.startsWith('terraform-'));
  }, [repoType]);

  // Auto-select Angular App when templateSource is 'standard'
  React.useEffect(() => {
    // Only run when templateSource is 'standard'
    // This prevents restoring/auto-selecting when user switches to GitHub
    if (templateSource !== 'standard') {
      return;
    }

    // Restore from sessionStorage when returning to form
    if (!formData) {
      const savedFormState = sessionStorage.getItem('github-repo-form-state');

      if (savedFormState) {
        try {
          const parsedState = JSON.parse(savedFormState);
          const ONE_HOUR = 60 * 60 * 1000;
          const isStale =
            parsedState.timestamp &&
            Date.now() - parsedState.timestamp > ONE_HOUR;

          if (!isStale && parsedState.standardTemplate) {
            // Only restore if the template is available for current repoType
            const isTemplateAvailable = availableTemplates.some(
              t => t.id === parsedState.standardTemplate,
            );
            if (isTemplateAvailable) {
              onChange(parsedState.standardTemplate);
              return; // Skip auto-select if restored
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to restore standardTemplate field:', error);
        }
      }
    }

    if (!formData) {
      onChange('angular-app');
    }
  }, [templateSource, formData, onChange, availableTemplates]);

  // Reset to angular-app if current selection is not available for the repo type
  React.useEffect(() => {
    if (
      formData &&
      formData.startsWith('terraform-') &&
      repoType !== 'Standard'
    ) {
      // Terraform template selected but repo type is not Standard - reset to angular-app
      onChange('angular-app');
    }
  }, [repoType, formData, onChange]);

  // Clear value when templateSource is not 'standard'
  // Using useLayoutEffect for synchronous execution before browser paint
  // This prevents stale standardTemplate values from showing in the Review page
  useLayoutEffect(() => {
    if (templateSource !== 'standard') {
      // Only clear once per transition to avoid redundant calls
      if (!hasClearedRef.current) {
        hasClearedRef.current = true;
        // Cast to unknown first since Backstage's onChange type doesn't permit undefined,
        // but passing undefined is the correct way to clear the field value
        onChange(undefined as unknown as string);
      }
    } else {
      // Reset the cleared flag when we're back in 'standard' mode
      hasClearedRef.current = false;
    }
  }, [templateSource, onChange]);

  // Only show when templateSource is 'standard'
  if (templateSource !== 'standard') {
    return null;
  }

  const handleChange = (event: any) => {
    onChange(event.target.value || undefined);
  };

  const getHelperText = () => {
    if (rawErrors && rawErrors.length > 0) {
      return rawErrors.join(', ');
    }
    if (formData) {
      return availableTemplates.find(t => t.id === formData)?.description;
    }
    return 'Choose a pre-configured template type';
  };

  return (
    <FormControl
      fullWidth
      required={required}
      error={rawErrors && rawErrors.length > 0}
    >
      <InputLabel id="standard-template-label">Standard Template</InputLabel>
      <Select
        labelId="standard-template-label"
        value={formData || 'angular-app'}
        onChange={handleChange}
        label="Standard Template"
      >
        {availableTemplates.map(template => (
          <MenuItem key={template.id} value={template.id}>
            {template.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{getHelperText()}</FormHelperText>
    </FormControl>
  );
};

export const StandardTemplatePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'StandardTemplatePicker',
    component: StandardTemplatePicker,
  }),
);
